import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormInputField, lt } from '@mews/framework';
import { ContentSection, createOption } from '@mews-commander/ui';
import { SelectField } from '@mews-commander/form';
import { useCurrentEnterpriseTaxations } from '@mews-commander/legacy-entities/src/selectors/taxEnvironments';
import { Typography } from '@optimus-web/core';
import { useLocalizeTaxRate } from '../../../hooks/useLocalizeTaxRate';
import { ProductFormValues } from '../../schemas/productFormSchema';
import { useTaxExemptionReasons } from '../../../hooks/useTaxExemptionReasons';

const TaxationSelectFields = () => {
    const taxations = useCurrentEnterpriseTaxations();
    const localizeTaxRate = useLocalizeTaxRate();

    const { setValue } = useFormContext<ProductFormValues>();

    return (
        <>
            {taxations.map(({ code: taxationCode, taxRateCodes, name }) =>
                taxRateCodes ? (
                    <SelectField
                        key={taxationCode}
                        name={`taxRates.${taxationCode}`}
                        label={name}
                        options={pipe(
                            taxRateCodes,
                            array.filterMap(localizeTaxRate),
                            array.map(({ label, code }) => createOption({ label, value: code }))
                        )}
                        onChange={(_, selected) => {
                            if (selected !== null) {
                                setValue('taxExemptionReasonCode', null);
                                setValue('taxExemptionLegalReference', null);
                            }
                        }}
                        emptyValueAsNull
                    />
                ) : null
            )}
        </>
    );
};

export const TaxSection = () => {
    const taxExemptionReasons = useTaxExemptionReasons();
    const taxationFormValues = useWatch<ProductFormValues>({ name: 'taxRates' });

    const taxExemptionReasonOptions = pipe(
        Object.values(taxExemptionReasons),
        array.filterMap(({ code, name }) =>
            code ? option.some(createOption({ label: name, value: code })) : option.none
        )
    );

    const showTaxExemptionFields =
        !taxationFormValues ||
        (taxationFormValues &&
            Object.values(taxationFormValues).every((taxRateValue) => !taxRateValue) &&
            array.isNonEmpty(taxExemptionReasonOptions));

    return (
        <ContentSection title={lt('Tax')} spacing="300" contentSpacing="200">
            <TaxationSelectFields />
            {showTaxExemptionFields ? (
                <SelectField
                    name="taxExemptionReasonCode"
                    label={lt('TaxExemptionReason')}
                    options={taxExemptionReasonOptions}
                    emptyValueAsNull
                />
            ) : null}
            {showTaxExemptionFields ? (
                <FormInputField label={lt('LegalReference')} name="taxExemptionLegalReference" />
            ) : null}
        </ContentSection>
    );
};

export const AllowanceTaxSection = () => (
    <ContentSection
        title={lt('Tax')}
        spacing="300"
        contentSpacing="200"
        description={lt('AllowanceFormTaxSectionDescription')}
    >
        <Typography textStyle="bodyLargeStrong" as="div">
            {lt('AllowanceFormTaxSubtitleProfit')}
        </Typography>
        <TaxationSelectFields />
    </ContentSection>
);
